<template>
  <div class="manual-add">
    <h1 style="margin-bottom: 12px">📝 手工录入账号</h1>

    <el-card>
      <template #header>
        <span>录入同行 / 意向客户抖音账号</span>
      </template>
      <el-form :model="form" label-width="130px" style="max-width: 760px">
        <el-form-item label="录入方式">
          <el-radio-group v-model="form.mode">
            <el-radio-button value="single">单个录入</el-radio-button>
            <el-radio-button value="batch">批量导入</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="录入类型" required>
          <el-radio-group v-model="form.target_type">
            <el-radio-button value="lead">🎯 意向客户</el-radio-button>
            <el-radio-button value="monitor">🛰️ 同行监控</el-radio-button>
          </el-radio-group>
          <div class="tip">
            <template v-if="form.target_type === 'lead'">意向客户 → 进入线索库，后续可私信跟进</template>
            <template v-else>同行监控 → 加入监控目标，自动盯新作品/开播截流</template>
          </div>
        </el-form-item>

        <!-- 单个录入 -->
        <template v-if="form.mode === 'single'">
          <el-form-item label="抖音主页链接">
            <el-input v-model="form.url" placeholder="https://www.douyin.com/user/xxxx 或 /video/xxxx" clearable />
            <div class="tip">填主页链接最准确；没有链接可用下方抖音号</div>
          </el-form-item>
          <el-form-item label="抖音号">
            <el-input v-model="form.douyin_id" placeholder="如 rebang21294（与主页链接二选一）" clearable />
          </el-form-item>
          <el-form-item label="昵称（可选）">
            <el-input v-model="form.nickname" placeholder="如 王姐美业工作室" clearable />
          </el-form-item>
          <el-form-item label="备注（可选）">
            <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="如：广州做设备的源头厂家 / 高意向询价客户" />
          </el-form-item>
        </template>

        <!-- 批量导入 -->
        <template v-else>
          <el-form-item label="批量账号">
            <el-input
              v-model="form.batch_text"
              type="textarea"
              :rows="8"
              placeholder="每行一个账号，支持以下格式：&#10;1. 主页链接：https://www.douyin.com/user/xxxx&#10;2. 抖音号：rebang21294&#10;3. 链接或抖音号 + 备注（用空格或逗号分隔）：https://www.douyin.com/user/xxxx 广州餐饮设备&#10;示例：&#10;MS4wLjABAAAAUmoO51z7qpfsdESXXiEI2KfK78KFE8Q3UsUPifpD9xY&#10;https://www.douyin.com/user/MS4wLjABAAAAvReOdbpXrATupnhZLpS9hqdq19lz8Ikwa_xrntx68uc 测试备注"
            />
            <div class="tip">每行一个账号，可一次粘贴几十个；链接最准，抖音号次之</div>
          </el-form-item>
        </template>

        <el-form-item v-if="form.target_type === 'monitor'" label="采集评论数">
          <el-input-number v-model="form.max_comments" :min="20" :max="500" :step="10" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="save">保存录入</el-button>
          <el-button @click="reset">清空</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 最近录入 -->
    <el-card style="margin-top: 20px">
      <template #header>
        <span>最近录入记录</span>
      </template>
      <el-table :data="recent" stripe size="small" empty-text="暂无手工录入记录">
        <el-table-column prop="created_at" label="时间" width="160" />
        <el-table-column prop="type_label" label="类型" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.type === 'lead' ? 'success' : 'primary'" size="small">
              {{ row.type_label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" width="160" show-overflow-tooltip />
        <el-table-column prop="url" label="链接" min-width="260" show-overflow-tooltip />
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'ManualAdd',
  data() {
    return {
      form: {
        mode: 'single',
        target_type: 'lead',
        url: '',
        douyin_id: '',
        nickname: '',
        remark: '',
        max_comments: 100,
        batch_text: '',
      },
      recent: [],
    }
  },
  async mounted() {
    await this.loadRecent()
  },
  methods: {
    // 解析批量文本：每行一个账号（链接 或 抖音号，可带备注）
    parseBatch() {
      const items = []
      const lines = (this.form.batch_text || '').split(/\r?\n/)
      for (const line of lines) {
        const t = line.trim()
        if (!t) continue
        // 分离备注：链接/抖音号 + 空格或逗号 + 备注
        const parts = t.split(/[\s,，]+/)
        const first = parts[0].trim()
        const remark = parts.slice(1).join(' ').trim()
        let url = '', douyin_id = ''
        if (first.startsWith('http')) {
          url = first
        } else {
          douyin_id = first
        }
        items.push({ url, douyin_id, nickname: '', remark })
      }
      return items
    },
    async save() {
      let items
      if (this.form.mode === 'batch') {
        items = this.parseBatch()
        if (!items.length) {
          this.$message.warning('请输入至少一个账号')
          return
        }
      } else {
        if (!this.form.url && !this.form.douyin_id) {
          this.$message.warning('请填写主页链接或抖音号')
          return
        }
        items = [{
          url: this.form.url,
          douyin_id: this.form.douyin_id,
          nickname: this.form.nickname,
          remark: this.form.remark,
        }]
      }
      try {
        const payload = {
          target_type: this.form.target_type,
          account_id: 0,
          max_comments: this.form.max_comments,
          items,
        }
        const r = await api.post('/leads/manual-add', payload)
        if (r && r.success) {
          this.$message.success(r.detail)
          await this.loadRecent()
          this.reset()
        } else {
          this.$message.warning(r?.detail || '录入失败')
        }
      } catch (e) {
        this.$message.error('录入失败：' + (e.response?.data?.detail || e.message))
      }
    },
    reset() {
      this.form = {
        mode: 'single',
        target_type: 'lead',
        url: '',
        douyin_id: '',
        nickname: '',
        remark: '',
        max_comments: 100,
        batch_text: '',
      }
    },
    async loadRecent() {
      try {
        // 最近线索（手工录入的）
        const r = await api.get('/leads', { params: { limit: 10 } })
        const leads = r || []
        const items = leads
          .filter(l => (l.tags || []).includes('手工录入') || l.source_keyword === '手工录入')
          .map(l => ({
            type: 'lead',
            type_label: '意向客户',
            name: l.nickname || l.username || l.user_id,
            url: l.source_url || '',
            remark: l.notes || '',
            created_at: (l.created_at || '').slice(0, 16),
          }))
        this.recent = items
      } catch (e) {
        console.error('加载最近录入失败', e)
      }
    },
  },
}
</script>

<style scoped>
.manual-add {
  padding: 4px;
}
.tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}
</style>
